import { ErrorCodeType } from "application/models/IError";
import { GetError } from "utils/GetError";
import { Order } from "../../../domains/order/agreggates/order/Order";
import { FinancialTransactionModel } from "../../../domains/order/agreggates/payment/FinancialTransactionModel";
import { TransactionType } from "../../../domains/order/agreggates/payment/transactionType";
import { IOrderRepository } from "../../../domains/order/interfaces/repositories/IOrderRepository";
import { IHttpRepository } from "../../interfaces/IHttpRepository";
import { IPasswordPainel } from "../../interfaces/IPasswordPainel";
import {
  DadosPedidosPOS,
  PedidoPos,
  responseSyncPedidoPos,
} from "../../interfaces/PedidoMeep";
import { LocalStorageCashlessOfflineRepository } from "../localStorage/LocalStorageCashlessOfflineRepository";
import { LocalStorageSyncPendingsRepository } from "../localStorage/LocalStorageSyncPendingsRepository";
import { OrderRepositoryLocalStorage } from "../localStorage/OrderRepositoryLocalStorage";
import { HttpRepository } from "./_HttpRepository";
import { orderToPedidoPOS, pedidoPosToLog } from "./_Parses";
import { CashlessRepository } from "./CashlessRepository";

const httpRepository: IHttpRepository = HttpRepository();
const cashlessRepository = CashlessRepository();
const orderRepository = OrderRepositoryLocalStorage();
const cashlessOfflineRepository = LocalStorageCashlessOfflineRepository();
const syncPendingOrdersRepository = LocalStorageSyncPendingsRepository();

export const MeepOrderRepository = (
  addLog?: (log: string, data: any) => void
): IOrderRepository => {
  //Salvar pedidos na fila de sincronização
  const sendOrderAsync = async (
    order: Order,
    tipoPagamento: TransactionType,
    transacaoAdquirente?: FinancialTransactionModel
  ) => {
    const pedidoPos = orderToPedidoPOS(
      order,
      tipoPagamento,
      transacaoAdquirente
    );
    await syncPendingOrdersRepository.saveStorageOrder([pedidoPos]);
    if (pedidoPos.isOfflineRecharge) {
      await cashlessOfflineRepository.savePendentConfirmationRecordOfflineOrders(
        [pedidoPos]
      );
    }

    return pedidoPos;
  };

  const SincronizarPedidoPOSAsync = async (
    dadosPedidosPOS: DadosPedidosPOS
  ) => {
    addLog &&
      addLog(
        "Request SincronizarPedidoPOSAsync",
        pedidoPosToLog(
          JSON.parse(dadosPedidosPOS.sincronizarPedidoPOSInsertModel) ?? []
        )
      );
    let response = await httpRepository.postAsync<
      DadosPedidosPOS,
      responseSyncPedidoPos[]
    >("/Pagamento/SincronizarPedidoPOS", dadosPedidosPOS);
    // >("https://5a78a3c20b7b.ngrok-free.app/Promptus.Meep.Server/api/Pagamento/SincronizarPedidoPOS", dadosPedidosPOS);
    Promise.all(
      response.map((it, key) =>
        setTimeout(() => {
          orderRepository.add(
            { id: it.pedidoPOSId, isSynchronized: true } as Order,
            "Sincronizado com sucesso!"
          );
        }, key * 500)
      )
    );
    return response;
  };

  const getPasswordPainel = async (
    sessaoId: string,
    localId: string
  ): Promise<IPasswordPainel> => {
    return await httpRepository.getAsync<IPasswordPainel>(
      `PasswordPanel/NextOrderPasssoword/LocalClient/${localId}/Cashier/${sessaoId}`
    );
  };

  const syncOrdersAsync = async (localClienteId: string) => {
    const storagePedidosPOS =
      await syncPendingOrdersRepository.getStoragePendentOrder();

    const storagePedidosPOSConfirmationRecord =
      await cashlessOfflineRepository.getStoragePendentRecordConfirmationOrder();

    let responseSync: responseSyncPedidoPos[] = [];

    if (storagePedidosPOS && storagePedidosPOS?.length > 0) {
      // Caso pagamento seja pix, será sincronizado como debito
      const pedidoPosParaSincronizar = storagePedidosPOS.map((it) => ({
        ...it,
        tipoPagamento:
          it.tipoPagamento === TransactionType.pix
            ? TransactionType.debit
            : it.tipoPagamento,
      }));

      const dadosPedidoPOS: DadosPedidosPOS = {
        localClienteId: localClienteId,
        tipoDispositivo: 2,
        superAppEventId: pedidoPosParaSincronizar[0]?.superAppEventId,
        registroSessaoPOSId: pedidoPosParaSincronizar[0]?.registroSessaoPOSId,
        sincronizarPedidoPOSInsertModel: JSON.stringify(
          pedidoPosParaSincronizar ?? "[]"
        ),
      };
      addLog && addLog("Request syncOrdersAsync", pedidoPosToLog(pedidoPosParaSincronizar));
      const result = await SincronizarPedidoPOSAsync(dadosPedidoPOS);
      if (result) {
        responseSync = [...responseSync, ...result];
        syncPendingOrdersRepository.removeSendedOrderFromStorageQueueAsync(
          result
        );
      }
    }

    if (
      storagePedidosPOSConfirmationRecord &&
      storagePedidosPOSConfirmationRecord?.length > 0
    ) {
      // Caso pagamento seja pix, será sincronizado como debito
      const pedidosConfirmacaoPendente =
        storagePedidosPOSConfirmationRecord.map((it) => ({
          ...it,
          tipoPagamento:
            it.tipoPagamento === TransactionType.pix
              ? TransactionType.debit
              : it.tipoPagamento,
        }));

      const pedidoPosParaConfirmarGravacao = pedidosConfirmacaoPendente.filter(
        (it) => it.isDataRecorded && !it.isOfflineRechargeRecordConfirmed
      );

      if (pedidoPosParaConfirmarGravacao.length) {
        const ordersIdToConfirmRecord = pedidoPosParaConfirmarGravacao
          .map((it) => it.pedidoPOSId || "")
          .filter(
            (id) => !storagePedidosPOS?.map((i) => i.pedidoPOSId).includes(id)
          );

        addLog &&
          addLog("Request", pedidoPosToLog(pedidoPosParaConfirmarGravacao));
        const result = await cashlessRepository.confirmPendingRecord(
          ordersIdToConfirmRecord
        );
        if (result) {
          responseSync = [
            ...responseSync,
            ...result.ordersId.map((it) => ({ pedidoPOSId: it })),
          ];
          cashlessOfflineRepository.removeRecordedConfirmationOrderFromStorageQueueAsync(
            result.ordersId.map((it) => ({ pedidoPOSId: it }))
          );
        }
      }
    }

    return responseSync;
  };

  const syncOrdersByIdAsync = async (
    localClienteId: string,
    orderId: string
  ) => {
    const storagePedidosPOS =
      await syncPendingOrdersRepository.getStoragePendentOrder();

    const storagePedidosPOSConfirmationRecord =
      await cashlessOfflineRepository.getStoragePendentRecordConfirmationOrder();

    let responseSync: responseSyncPedidoPos[] = [];

    if (storagePedidosPOS && storagePedidosPOS?.length > 0) {
      // Caso pagamento seja pix, será sincronizado como debito
      const pedidoPos = storagePedidosPOS.filter(
        (item) => item.pedidoPOSId === orderId
      );
      const pedidoPosParaSincronizar = pedidoPos.map((it) => ({
        ...it,
        tipoPagamento:
          it.tipoPagamento === TransactionType.pix
            ? TransactionType.debit
            : it.tipoPagamento,
      }));

      const dadosPedidoPOS: DadosPedidosPOS = {
        localClienteId: localClienteId,
        tipoDispositivo: 2,
        superAppEventId: pedidoPosParaSincronizar[0]?.superAppEventId,
        registroSessaoPOSId: pedidoPosParaSincronizar[0]?.registroSessaoPOSId,
        sincronizarPedidoPOSInsertModel: JSON.stringify(
          pedidoPosParaSincronizar ?? "[]"
        ),
      };
      addLog && addLog("Request syncOrdersByIdAsync", pedidoPosToLog(pedidoPosParaSincronizar));
      const result = await SincronizarPedidoPOSAsync(dadosPedidoPOS);
      if (result) {
        responseSync = [...responseSync, ...result];
        syncPendingOrdersRepository.removeSendedOrderFromStorageQueueAsync(
          result
        );
      }
    }

    if (
      storagePedidosPOSConfirmationRecord &&
      storagePedidosPOSConfirmationRecord?.length > 0
    ) {
      // Caso pagamento seja pix, será sincronizado como debito
      const pedidoPos = storagePedidosPOSConfirmationRecord.filter(
        (item) => item.pedidoPOSId === orderId
      );
      const pedidoPosParaSincronizar = pedidoPos.map((it) => ({
        ...it,
        tipoPagamento:
          it.tipoPagamento === TransactionType.pix
            ? TransactionType.debit
            : it.tipoPagamento,
      }));

      const dadosPedidoPOS: DadosPedidosPOS = {
        localClienteId: localClienteId,
        tipoDispositivo: 2,
        superAppEventId: pedidoPosParaSincronizar[0]?.superAppEventId,
        registroSessaoPOSId: pedidoPosParaSincronizar[0]?.registroSessaoPOSId,
        sincronizarPedidoPOSInsertModel: JSON.stringify(
          pedidoPosParaSincronizar ?? "[]"
        ),
      };
      addLog && addLog("Request", pedidoPosToLog(pedidoPosParaSincronizar));
      const result = await SincronizarPedidoPOSAsync(dadosPedidoPOS);
      if (result) {
        responseSync = [...responseSync, ...result];
        syncPendingOrdersRepository.removeSendedOrderFromStorageQueueAsync(
          result
        );
      }
    }
    return responseSync;
  };

  const sendFreeOrdersAsync = async (
    order: Order,
    localClientId: string,
    tipoPagamento?: TransactionType,
    transacaoAdquirente?: FinancialTransactionModel
  ): Promise<PedidoPos> => {
    const pedidoPos: PedidoPos = orderToPedidoPOS(
      order,
      tipoPagamento,
      transacaoAdquirente
    );

    const pedidoPosParaSincronizar = {
      ...pedidoPos,
      tipoPagamento:
        tipoPagamento === TransactionType.pix
          ? TransactionType.debit
          : tipoPagamento,
    };

    const dadosPedidoPOS: DadosPedidosPOS = {
      localClienteId: localClientId,
      tipoDispositivo: 2,
      superAppEventId: pedidoPosParaSincronizar.superAppEventId,
      registroSessaoPOSId: pedidoPosParaSincronizar.registroSessaoPOSId,
      sincronizarPedidoPOSInsertModel: JSON.stringify([
        pedidoPosParaSincronizar,
      ]),
    };

    const responseSync = await SincronizarPedidoPOSAsync(dadosPedidoPOS);

    if (responseSync.find((res) => res.pedidoPOSId === order.id)) {
      return pedidoPos;
    } else {
      throw GetError(
        "Pedido Não enviado",
        "onSucessPayment",
        "SyncOrderUseCase",
        "Falha ao sincronizar pedido",
        ErrorCodeType.ORDER
      );
    }
  };

  return {
    sendOrderAsync,
    syncOrdersAsync,
    syncOrdersByIdAsync,
    getPasswordPainel,
    sendFreeOrdersAsync,
  };
};
